import { CalendarFragmentKey, CalendarSurface } from '../model';
import { CalendarFragmentDefinition, supportsSurface } from './types';
import * as Card from './cardFragments';
import * as Detail from './detailFragments';

/**
 * The catalogue of placeable attributes. This is the single source of truth for what can appear on
 * a card or in the detail popover — the renderers, the editor's palette and the translated labels
 * all read from here, so adding an attribute means adding one entry.
 *
 * A surface with no renderer cannot host the fragment; the editor filters its palette on that.
 */
export const CALENDAR_FRAGMENTS: CalendarFragmentDefinition[] = [
  {
    key: CalendarFragmentKey.ApplicationId,
    labelKey: 'CalendarLayout.fragment.applicationId',
    labelDefault: 'Reservation number',
    card: Card.CardApplicationId,
    detail: Detail.DetailApplicationId,
  },
  {
    key: CalendarFragmentKey.WorkflowIcon,
    labelKey: 'CalendarLayout.fragment.workflowIcon',
    labelDefault: 'Status icon',
    card: Card.CardWorkflowIcon,
  },
  {
    key: CalendarFragmentKey.WorkflowState,
    labelKey: 'CalendarLayout.fragment.workflowState',
    labelDefault: 'Status',
    detail: Detail.DetailWorkflowState,
  },
  {
    key: CalendarFragmentKey.Title,
    labelKey: 'CalendarLayout.fragment.title',
    labelDefault: 'On-behalf-of, or the reference when there is none',
    card: Card.CardTitle,
    detail: Detail.DetailTitle,
  },
  {
    key: CalendarFragmentKey.Name,
    labelKey: 'CalendarLayout.fragment.name',
    labelDefault: 'Reference and order',
    card: Card.CardName,
    detail: Detail.DetailName,
  },
  {
    key: CalendarFragmentKey.OnBehalfOf,
    labelKey: 'CalendarLayout.fragment.onBehalfOf',
    labelDefault: 'On behalf of',
    card: Card.CardOnBehalfOf,
    detail: Detail.DetailOnBehalfOf,
  },
  {
    key: CalendarFragmentKey.Flags,
    labelKey: 'CalendarLayout.fragment.flags',
    labelDefault: 'Flags',
    card: Card.CardFlags,
    detail: Detail.DetailFlags,
  },
  {
    key: CalendarFragmentKey.ActionTypes,
    labelKey: 'CalendarLayout.fragment.actionTypes',
    labelDefault: 'Loading / unloading',
    card: Card.CardActionTypes,
    detail: Detail.DetailActionTypes,
  },
  {
    key: CalendarFragmentKey.Labels,
    labelKey: 'CalendarLayout.fragment.labels',
    labelDefault: 'Labels',
    card: Card.CardLabels,
    detail: Detail.DetailLabels,
  },
  {
    key: CalendarFragmentKey.Location,
    labelKey: 'CalendarLayout.fragment.location',
    labelDefault: 'Location',
    supportsIcon: true,
    card: Card.CardLocation,
    detail: Detail.DetailLocation,
  },
  {
    key: CalendarFragmentKey.CustomerName,
    labelKey: 'CalendarLayout.fragment.customerName',
    labelDefault: 'Customer',
    supportsIcon: true,
    card: Card.CardCustomerName,
    detail: Detail.DetailCustomerName,
  },
  {
    // Card only: on the detail popover the driver is already part of the carrier block.
    key: CalendarFragmentKey.Driver,
    labelKey: 'CalendarLayout.fragment.driver',
    labelDefault: 'Driver',
    supportsIcon: true,
    card: Card.CardDriver,
  },
  {
    key: CalendarFragmentKey.OrderNote,
    labelKey: 'CalendarLayout.fragment.orderNote',
    labelDefault: 'Order note',
    supportsIcon: true,
    card: Card.CardOrderNote,
    detail: Detail.DetailOrderNote,
  },
  {
    key: CalendarFragmentKey.CargoWeight,
    labelKey: 'CalendarLayout.fragment.cargoWeight',
    labelDefault: 'Total weight',
    supportsIcon: true,
    card: Card.CardCargoWeight,
    detail: Detail.DetailCargoWeight,
  },
  {
    key: CalendarFragmentKey.CargoVolume,
    labelKey: 'CalendarLayout.fragment.cargoVolume',
    labelDefault: 'Total volume',
    supportsIcon: true,
    card: Card.CardCargoVolume,
    detail: Detail.DetailCargoVolume,
  },
  {
    key: CalendarFragmentKey.LinkedExpedition,
    labelKey: 'CalendarLayout.fragment.linkedExpedition',
    labelDefault: 'Linked expedition',
    supportsIcon: true,
    card: Card.CardLinkedExpedition,
    detail: Detail.DetailLinkedExpedition,
  },
  {
    key: CalendarFragmentKey.LinkedSupplierNotification,
    labelKey: 'CalendarLayout.fragment.linkedSupplierNotification',
    labelDefault: 'Linked supplier notification',
    supportsIcon: true,
    card: Card.CardLinkedSupplierNotification,
    detail: Detail.DetailLinkedSupplierNotification,
  },
  {
    key: CalendarFragmentKey.CarrierInfo,
    labelKey: 'CalendarLayout.fragment.carrierInfo',
    labelDefault: 'Carrier and vehicle',
    supportsIcon: true,
    card: Card.CardCarrierInfo,
    detail: Detail.DetailCarrierInfo,
  },
  {
    key: CalendarFragmentKey.CargoItem,
    labelKey: 'CalendarLayout.fragment.cargoItem',
    labelDefault: 'Cargo',
    supportsIcon: true,
    card: Card.CardCargoItem,
    detail: Detail.DetailCargoItem,
  },
  {
    key: CalendarFragmentKey.TimeRange,
    labelKey: 'CalendarLayout.fragment.timeRange',
    labelDefault: 'Date and time',
    supportsIcon: true,
    detail: Detail.DetailTimeRange,
  },
  {
    key: CalendarFragmentKey.SlotDuration,
    labelKey: 'CalendarLayout.fragment.slotDuration',
    labelDefault: 'Time and duration',
    card: Card.CardSlotDuration,
    detail: Detail.DetailSlotDuration,
  },
  {
    key: CalendarFragmentKey.Duration,
    labelKey: 'CalendarLayout.fragment.duration',
    labelDefault: 'Duration',
    detail: Detail.DetailDuration,
  },
  {
    key: CalendarFragmentKey.Created,
    labelKey: 'CalendarLayout.fragment.created',
    labelDefault: 'Created by',
    card: Card.CardCreated,
    detail: Detail.DetailCreated,
  },
  {
    key: CalendarFragmentKey.Updated,
    labelKey: 'CalendarLayout.fragment.updated',
    labelDefault: 'Updated by',
    card: Card.CardUpdated,
    detail: Detail.DetailUpdated,
  },
];

const BY_KEY = new Map<CalendarFragmentKey, CalendarFragmentDefinition>(CALENDAR_FRAGMENTS.map(definition => [definition.key, definition]));

export const findFragment = (key: CalendarFragmentKey): CalendarFragmentDefinition | undefined => BY_KEY.get(key);

export const fragmentsForSurface = (surface: CalendarSurface): CalendarFragmentDefinition[] =>
  CALENDAR_FRAGMENTS.filter(definition => supportsSurface(definition, surface));

export const rendererFor = (key: CalendarFragmentKey, surface: CalendarSurface) => {
  const definition = BY_KEY.get(key);
  if (!definition) return undefined;
  return surface === 'card' ? definition.card : definition.detail;
};
